import { warn } from '@execonline-inc/logging';
import { pipe } from '@kofno/piper';
import { Task } from 'taskarian';
import { errorMessage } from '../../ExceptionHandling';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';

export interface QuickSightVisualization {
  dashboard?: any; // The embedded dashboard experience object
}

// Function that takes container element, embed URL, and options
export type quickSightVizFn = (
  container: HTMLElement,
  embedUrl: string,
  options: any,
) => QuickSightVisualization;

export interface QuickSightEmbedAPILoadError {
  kind: 'quicksight-embed-api-load-error';
  key: string;
}

export const quickSightEmbedAPILoadError = (key: string): QuickSightEmbedAPILoadError => ({
  kind: 'quicksight-embed-api-load-error',
  key,
});

// Helper function to create the QuickSight visualization embedding function
const createQuickSightEmbedFunction = (): quickSightVizFn => {
  return (container, embedUrl, options) => {
    const frameOptions = {
      url: embedUrl,
      container: container,
      height: options.height || '700px',
      width: options.width || '100%',
      onChange: options.onChange,
      onError: options.onError,
    };

    const contentOptions = {
      parameters: options.parameters || [],
      locale: options.locale || 'en-US',
    };

    const visualization: QuickSightVisualization = {
      dashboard: undefined,
    };

    // Handle the promise internally
    createEmbeddingContext()
      .then((context) => {
        return context.embedDashboard(frameOptions, contentOptions);
      })
      .then((dashboardExperience) => {
        // Store the dashboard experience for later use
        visualization.dashboard = dashboardExperience;

        if (options.onSuccess) {
          options.onSuccess(dashboardExperience);
        }
      })
      .catch((error) => {
        console.error('Error embedding QuickSight dashboard:', error);
        if (options.onError) {
          options.onError(error);
        }
      });

    return visualization;
  };
};

export const loadQuickSightEmbedAPI = (): Task<QuickSightEmbedAPILoadError, quickSightVizFn> => {
  return new Task<QuickSightEmbedAPILoadError, quickSightVizFn>((reject, resolve) => {
    const rejectException = pipe(errorMessage, quickSightEmbedAPILoadError, reject);
    try {
      resolve(createQuickSightEmbedFunction());
    } catch (e) {
      rejectException(e);
    }

    return () => {
      warn('Loading the QuickSight Embed API cannot be canceled');
    };
  });
};

// Note: QuickSight Embedding SDK does not support extracting actual visual data.
// The SDK can only retrieve visual IDs and basic metadata, not the rendered data values.
// For data extraction, we query the data warehouse directly via the backend.

/**
 * Extract current parameter/filter values from the embedded QuickSight dashboard
 * @param dashboard The embedded dashboard experience object
 * @returns Task that resolves to the current parameters
 */
/**
 * Helper function to get parameters with retry logic
 */
const getParametersWithRetry = (
  dashboard: any,
  retries: number = 3,
  delayMs: number = 1000,
): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const attempt = (remainingRetries: number) => {
      dashboard
        .getParameters()
        .then((parameters: any[]) => {
          resolve(parameters);
        })
        .catch((error: any) => {
          if (remainingRetries > 0) {
            console.warn(
              `⚠️ getParameters failed, retrying... (${retries - remainingRetries + 1}/${retries})`,
              error?.message || String(error),
            );
            setTimeout(() => attempt(remainingRetries - 1), delayMs);
          } else {
            reject(error);
          }
        });
    };

    attempt(retries);
  });
};

export const getDashboardParameters = (dashboard: any): Task<Error, Record<string, any>> => {
  return new Task<Error, Record<string, any>>((reject, resolve) => {
    if (!dashboard) {
      reject(new Error('Dashboard not initialized'));
      return () => {};
    }

    // Check if getParameters method exists
    if (typeof dashboard.getParameters !== 'function') {
      console.warn(
        '⚠️ dashboard.getParameters is not a function. Available methods:',
        Object.keys(dashboard),
      );
      reject(new Error('getParameters method not available on dashboard object'));
      return () => {};
    }

    // Try to get parameters with retry logic (3 attempts with 1 second delay)
    getParametersWithRetry(dashboard, 3, 1000)
      .then((parameters: any[]) => {
        // Convert parameter array to key-value object
        const paramMap: Record<string, any> = {};

        if (!parameters || !Array.isArray(parameters)) {
          console.warn('⚠️ Parameters is not an array:', parameters);
          resolve({});
          return;
        }

        parameters.forEach((param: any) => {
          const name = param.Name || param.name;
          const values = param.Values || param.values || [];

          // If single value, use it directly; if multiple, keep as array
          paramMap[name] = values.length === 1 ? values[0] : values;
        });

        resolve(paramMap);
      })
      .catch((error: any) => {
        console.error('❌ Error getting parameters after retries:', error);
        reject(new Error(`Failed to get dashboard parameters: ${error?.message || String(error)}`));
      });

    return () => {
      warn('Getting dashboard parameters cannot be canceled');
    };
  });
};
